// Forms.jsx
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { Form } from 'react-final-form';
import { useHistory, useParams } from 'react-router-dom';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import { Card, CardBody } from '@spark/ui-components/components/Card';
import { FormButtonToolbar, FormContainer } from '@spark/ui-components/components/form/FormElements';
import { notification } from '@spark/ui-components/utils/notification';
import DynamicForm from '@spark/ui-components/components/form/DynamicForm';
import { formStructure, formSections, validateForm } from './formConfig';

// Serviços
import { getBeneficioById, createBeneficio, updateBeneficio } from '@spark/services/recrutamento-rh';

const Forms = ({ isReadOnly = false }) => {
  const history = useHistory();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [initialData, setInitialData] = useState(null);

  const isEditMode = !!id && id !== 'criar' && !isReadOnly;

  // 🔹 Carregar dados para edição/detalhe
  useEffect(() => {
    let isActive = true;

    if ((isReadOnly || isEditMode) && id && id !== 'criar') {
      const load = async () => {
        try {
          setLoadingData(true);
          const data = await getBeneficioById(id);
          if (isActive) {
            setInitialData({
              Categoria: data?.Categoria ?? '',
              Nome: data?.Nome ?? '',
            });
          }
        } catch (err) {
          console.error('Erro ao carregar benefício:', err);
          if (isActive) {
            notification.error('Não foi possível carregar o benefício.');
          }
        } finally {
          if (isActive) {
            setLoadingData(false);
          }
        }
      };
      load();
    }
    return () => {
      isActive = false;
    };
  }, [isReadOnly, isEditMode, id]);

  const defaultValues = useMemo(
    () => ({
      Categoria: '',
      Nome: '',
    }),
    []
  );

  const initialValues = initialData || defaultValues;

  const onSubmit = useCallback(
    async (values) => {
      try {
        setLoading(true);

        if (isEditMode) {
          const payload = {
            Categoria: values.Categoria?.trim() || '',
            Nome: values.Nome?.trim() || '',
          };
          await updateBeneficio(id, payload);
          notification.success('Benefício atualizado com sucesso!');
        } else {
          const payload = {
            categoria: values.Categoria?.trim() || '',
            nome: values.Nome?.trim() || '',
          };
          await createBeneficio(payload);
          notification.success('Benefício criado com sucesso!');
        }

        history.goBack();
      } catch (err) {
        console.error(err);
        notification.error('Erro ao salvar o benefício. Tente novamente.');
      } finally {
        setLoading(false);
      }
    },
    [history, id, isEditMode]
  );

  if ((isReadOnly || isEditMode) && loadingData) {
    return (
      <Card>
        <CardBody>
          <div style={{ textAlign: 'center', padding: 50 }}>Carregando dados do benefício...</div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Form onSubmit={onSubmit} initialValues={initialValues} validate={validateForm}>
      {({ handleSubmit }) => (
        <Card>
          <CardBody>
            <FormContainer onSubmit={handleSubmit}>
              <DynamicForm
                structure={formStructure}
                sections={formSections(initialValues)}
                isViewMode={isReadOnly}
                isEditing={isEditMode}
              />

              {!isReadOnly && (
                <FormButtonToolbar className="mt-5">
                  <ButtonWithLoading
                    variant="primary"
                    type="submit"
                    loading={loading ? 'true' : undefined}
                    disabled={loading}
                  >
                    {isEditMode ? 'Atualizar' : 'Salvar'}
                  </ButtonWithLoading>

                  <Button
                    variant="secondary"
                    type="button"
                    onClick={() => history.goBack()}
                  >
                    Cancelar
                  </Button>
                </FormButtonToolbar>
              )}
            </FormContainer>
          </CardBody>
        </Card>
      )}
    </Form>
  );
};

export default Forms;
